import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../Firebase';
import { BookOpen, Brain, ChevronRight, ChevronDown, CheckCircle, Clock } from 'lucide-react';
import { COURSES_DATA, QUIZ_DATA } from '../AdminData';
import etuBg from './etu1.jpg';

const NIVEAUX = ['A1','A2','B1','B2','C1','C2'];

export default function AdminCours({ staff }) {
  const [niveau, setNiveau]           = useState('A1');
  const [activeTab, setActiveTab]     = useState('cours');
  const [openModule, setOpenModule]   = useState(null);
  const [activeCourse, setActiveCourse] = useState(null);
  const [activeQuiz, setActiveQuiz]   = useState(null);
  const [progressions, setProgressions] = useState([]);
  const [loading, setLoading]         = useState(false);

  const courseData = COURSES_DATA[niveau] || COURSES_DATA['A1'];

  useEffect(() => {
    setActiveCourse(null);
    setActiveQuiz(null);
    setOpenModule(Object.keys((COURSES_DATA[niveau] || COURSES_DATA['A1']).categories)[0]);
  }, [niveau]);

  useEffect(() => {
    if (activeTab === 'progression') fetchProgressions();
  }, [activeTab, niveau]);

  async function fetchProgressions() {
    setLoading(true);
    try {
      const etSnap = await getDocs(collection(db, 'etudiants'));
      const all = [];
      for (const etDoc of etSnap.docs) {
        const etData = etDoc.data();
        if (etData.niveau !== niveau) continue;
        const progSnap = await getDocs(collection(db, 'etudiants', etDoc.id, 'progression'));
        const progs = {};
        progSnap.forEach(d => { progs[d.id] = d.data(); });
        all.push({ ...etData, id: etDoc.id, progression: progs });
      }
      setProgressions(all);
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  const renderCourseContent = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## '))  return <h3 key={i} className="admin-cours-h2">{line.replace('## ','')}</h3>;
      if (line.startsWith('### ')) return <h4 key={i} className="admin-cours-h3">{line.replace('### ','')}</h4>;
      if (line.startsWith('> '))   return <blockquote key={i} className="admin-cours-quote">{line.replace('> ','')}</blockquote>;
      if (line.startsWith('- '))   return <li key={i} className="admin-cours-li" dangerouslySetInnerHTML={{__html:line.replace('- ','').replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')}}/>;
      if (line.trim() === '')      return <br key={i}/>;
      if (line.startsWith('|'))    return <p key={i} style={{fontFamily:'monospace',fontSize:'0.8rem',color:'var(--admin-muted)',background:'var(--admin-bg)',padding:'0.2rem 0.5rem',borderRadius:4}}>{line}</p>;
      return <p key={i} className="admin-cours-para" dangerouslySetInnerHTML={{__html:line.replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>').replace(/\*(.+?)\*/g,'<em>$1</em>')}}/>;
    });
  };

  return (
    <div className="admin-page admin-page-bg admin-page-bg--cours" style={{backgroundSize:"cover",backgroundPosition:"center",backgroundAttachment:"fixed",backgroundBlendMode:'overlay',backgroundColor:"rgba(244,245,247,0.93)"}}>
      <div style={{backgroundImage:`url(${etuBg})`,backgroundSize:"cover",backgroundPosition:"center",position:"fixed",inset:0,opacity:0.15,pointerEvents:"none",zIndex:0}}/>

      <div className="admin-page__header">
        <div>
          <h2>Cours & Quiz</h2>
          <p>Consultez les cours, quiz et progression des étudiants</p>
        </div>
        <select className="admin-select" value={niveau} onChange={e => setNiveau(e.target.value)}>
          {NIVEAUX.map(n => <option key={n} value={n}>Niveau {n}</option>)}
        </select>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        {[['cours','📚 Cours'],['quiz','🧠 Quiz'],['progression','📊 Progression']].map(([k,l]) => (
          <button key={k} className={`admin-tab ${activeTab===k?'active':''}`}
            onClick={() => { setActiveTab(k); setActiveCourse(null); setActiveQuiz(null); }}>{l}
          </button>
        ))}
      </div>

      {/* ── COURS ── */}
      {activeTab === 'cours' && (
        <div className="admin-cours-layout">
          {/* Sidebar */}
          <div className="admin-cours-sidebar">
            {Object.entries(courseData.categories).map(([module, catData], mi) => (
              <div key={module} className="admin-cours-module">
                <button
                  className={`admin-cours-module__header ${openModule===module?'open':''}`}
                  onClick={() => setOpenModule(openModule===module?null:module)}
                >
                  <span className="admin-cours-module__num">Module {mi+1}</span>
                  <span className="admin-cours-module__name">{module}</span>
                  <ChevronDown size={15} className="admin-cours-module__arrow"/>
                </button>
                {openModule === module && (
                  <div className="admin-cours-module__list">
                    {catData.courses.map((course, ci) => (
                      <button key={course.id}
                        className={`admin-cours-item ${activeCourse?.id===course.id?'active':''}`}
                        onClick={() => setActiveCourse({...course, module})}
                      >
                        <span className="admin-cours-item__num">{mi+1}.{ci+1}</span>
                        <div>
                          <span className="admin-cours-item__title">{course.title}</span>
                          <span className="admin-cours-item__dur"><Clock size={11}/> {course.duration}</span>
                        </div>
                        <ChevronRight size={14}/>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contenu */}
          <div className="admin-cours-content">
            {!activeCourse ? (
              <div className="admin-cours-empty">
                <BookOpen size={48}/>
                <p>Sélectionnez un cours dans le menu</p>
              </div>
            ) : (
              <div className="admin-cours-detail">
                <div className="admin-cours-detail__header">
                  <span className="admin-badge admin-badge--niveau">{activeCourse.module}</span>
                  <h2>{activeCourse.title}</h2>
                  <span className="admin-cours-detail__dur"><Clock size={14}/> {activeCourse.duration}</span>
                </div>
                <div className="admin-cours-detail__content">
                  {renderCourseContent(activeCourse.content)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── QUIZ ── */}
      {activeTab === 'quiz' && (
        <div className="admin-cours-layout">
          {/* Sidebar */}
          <div className="admin-cours-sidebar">
            {Object.entries(courseData.categories).map(([module, catData], mi) => (
              <div key={module} className="admin-cours-module">
                <button
                  className={`admin-cours-module__header ${openModule===module?'open':''}`}
                  onClick={() => setOpenModule(openModule===module?null:module)}
                >
                  <span className="admin-cours-module__num">Module {mi+1}</span>
                  <span className="admin-cours-module__name">{module}</span>
                  <ChevronDown size={15} className="admin-cours-module__arrow"/>
                </button>
                {openModule === module && (
                  <div className="admin-cours-module__list">
                    {catData.courses.map((course, ci) => {
                      const qData = QUIZ_DATA?.courses?.[course.id];
                      return (
                        <button key={course.id}
                          className={`admin-cours-item ${activeQuiz?.id===course.id?'active':''}`}
                          onClick={() => qData && setActiveQuiz({...qData, id:course.id, module})}
                          disabled={!qData}
                        >
                          <span className="admin-cours-item__num">{mi+1}.{ci+1}</span>
                          <div>
                            <span className="admin-cours-item__title">{course.title}</span>
                            <span className="admin-cours-item__dur">
                              <Brain size={11}/> {qData?.questions?.length||0} questions
                            </span>
                          </div>
                          <ChevronRight size={14}/>
                        </button>
                      );
                    })}
                    {/* Quiz module */}
                    {QUIZ_DATA?.modules?.[module] && (
                      <button
                        className={`admin-cours-item admin-cours-item--module ${activeQuiz?.id===`mod_${module}`?'active':''}`}
                        onClick={() => setActiveQuiz({...QUIZ_DATA.modules[module], id:`mod_${module}`, module, isModule:true})}
                      >
                        <span className="admin-cours-item__num">📋</span>
                        <div>
                          <span className="admin-cours-item__title">Quiz du Module</span>
                          <span className="admin-cours-item__dur">
                            <Brain size={11}/> {QUIZ_DATA.modules[module].questions.length} questions
                          </span>
                        </div>
                        <ChevronRight size={14}/>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {/* Examen final */}
            {QUIZ_DATA?.final?.A1 && (
              <div className="admin-cours-module">
                <button
                  className={`admin-cours-item admin-cours-item--final ${activeQuiz?.id==='final_A1'?'active':''}`}
                  onClick={() => setActiveQuiz({...QUIZ_DATA.final.A1, id:'final_A1', isFinal:true})}
                >
                  <span>⭐</span>
                  <div>
                    <span className="admin-cours-item__title">Examen Final {niveau}</span>
                    <span className="admin-cours-item__dur"><Brain size={11}/> 120 questions</span>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* Questions avec toutes les réponses visibles */}
          <div className="admin-cours-content">
            {!activeQuiz ? (
              <div className="admin-cours-empty">
                <Brain size={48}/>
                <p>Sélectionnez un quiz dans le menu</p>
              </div>
            ) : (
              <div className="admin-quiz-detail">
                <div className="admin-quiz-detail__header">
                  <h2>{activeQuiz.title}</h2>
                  <span className="admin-badge admin-badge--niveau">{activeQuiz.questions?.length} questions</span>
                  <span className={`admin-badge ${activeQuiz.isFinal?'admin-badge--warn':activeQuiz.isModule?'admin-badge--info':'admin-badge--ok'}`}>
                    Seuil : {activeQuiz.isFinal?'75%':'50%'}
                  </span>
                </div>
                <div className="admin-quiz-questions">
                  {activeQuiz.questions?.map((q, qi) => (
                    <div key={qi} className="admin-quiz-question">
                      <div className="admin-quiz-question__num">Q{qi+1}</div>
                      <div className="admin-quiz-question__body">
                        <p className="admin-quiz-question__text">{q.q}</p>
                        <div className="admin-quiz-question__opts">
                          {(q.opts || q.options || []).map((opt, oi) => (
                            <div key={oi} className={`admin-quiz-answer ${oi === (q.a ?? q.answer) ? 'correct' : ''}`}>
                              <span className="admin-quiz-answer__letter">{['A','B','C','D'][oi]}</span>
                              <span>{opt}</span>
                              {oi === (q.a ?? q.answer) && <CheckCircle size={14} className="admin-quiz-answer__check"/>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── PROGRESSION ── */}
      {activeTab === 'progression' && (
        loading ? <div className="admin-page-loading"><div className="admin-spinner"/></div> :
        <div className="admin-table-box">
          <table className="admin-table">
            <thead>
              <tr><th>Étudiant</th><th>Quiz réussis</th><th>Score moyen</th><th>Examen final</th><th>Statut</th></tr>
            </thead>
            <tbody>
              {progressions.length === 0 && (
                <tr><td colSpan={5} style={{textAlign:'center',padding:'2rem',color:'var(--admin-muted)'}}>Aucun étudiant à ce niveau</td></tr>
              )}
              {progressions.map(e => {
                const prog   = e.progression || {};
                const passed = Object.values(prog).filter(p => p.passed && p.type==='course').length;
                const scores = Object.values(prog).filter(p => p.bestPct).map(p => p.bestPct);
                const avg    = scores.length ? Math.round(scores.reduce((a,b)=>a+b,0)/scores.length) : 0;
                const final  = prog[`final_${niveau}`];
                return (
                  <tr key={e.id}>
                    <td><strong>{e.nom}</strong><br/><small>{e.email}</small></td>
                    <td>{passed} quiz</td>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                        <div style={{flex:1,height:6,background:'var(--admin-border)',borderRadius:4,overflow:'hidden'}}>
                          <div style={{height:'100%',width:`${avg}%`,background:avg>=50?'#2f9e44':'#C9A84C',borderRadius:4}}/>
                        </div>
                        <strong style={{fontSize:'0.82rem'}}>{avg}%</strong>
                      </div>
                    </td>
                    <td>{final?<span className={`admin-badge ${final.bestPct>=75?'admin-badge--ok':'admin-badge--err'}`}>{final.bestPct}%</span>:<span className="admin-badge">Non passé</span>}</td>
                    <td><span className={`admin-badge ${e.actif?'admin-badge--ok':'admin-badge--err'}`}>{e.actif?'Actif':'Inactif'}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}